import { useEffect } from "react";
import { Control, Controller, UseFormSetValue, Path, useWatch } from "react-hook-form";

import useDialogStore from "@store/useDialogStore";
import { RelationObjectType } from "@enums/relations.enum";
import { IThemeOption } from "@interfaces/sourcing.interface";
import { ThemeComponentWithThemes } from "@components/SourcingThemes";
import { ConfirmDeleteDialog } from "@ui/Dialogs";
import { FieldErrorMessage } from "@ui/FormElements";

export interface IHasThemesField {
  themes: IThemeOption[];
}

interface ISourcingThemeFormFieldProps<T extends IHasThemesField> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  defaultValue?: any[];
  relatedEntityName?: string;
  onThemeAdd?: (theme: IThemeOption, removedParentIds: string[]) => void;
  onThemeRemove?: (theme: IThemeOption, addedParentId: string | null) => void;
}

export function SourcingThemeFormField<T extends IHasThemesField>({
  control,
  setValue,
  defaultValue,
  relatedEntityName,
  onThemeAdd,
  onThemeRemove,
}: ISourcingThemeFormFieldProps<T>) {
  const { openDialog, closeDialog } = useDialogStore();
  const formData = useWatch({ control });
  const themes = (formData?.themes ?? []) as T["themes"];

  const handleAddTheme = (theme: IThemeOption) => {
    const parentThemeIds = theme?.parent_list?.map((i) => i.id) || [];
    const removedParentIds = themes.filter((t) => parentThemeIds.includes(t.id)).map((t) => t.id);

    const updatedThemes = themes.filter((theme) => !parentThemeIds.includes(theme.id));

    setValue("themes" as Path<T>, [...updatedThemes, theme] as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
    onThemeAdd?.(theme, removedParentIds);
  };

  const handleRemoveTheme = (themeToRemove: IThemeOption, parentTheme: IThemeOption | null) => {
    let updatedThemes = themes.filter((theme) => theme.id !== themeToRemove.id);

    if (parentTheme) {
      updatedThemes.push(parentTheme);
    }

    setValue("themes" as Path<T>, updatedThemes as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
    onThemeRemove?.(themeToRemove, parentTheme?.id ?? null);
  };

  const handleOpenConfirmDeleteDialog = (
    themeToRemove: IThemeOption,
    parentTheme: IThemeOption | null
  ) => {
    if (relatedEntityName) {
      openDialog(
        <ConfirmDeleteDialog
          title="Remove theme from company?"
          onSubmit={() => {
            handleRemoveTheme(themeToRemove, parentTheme);
            closeDialog();
          }}
        >
          <p className="px-4 text-sm text-center">
            This will remove the <strong>{themeToRemove.title}</strong> theme from{" "}
            <strong>{relatedEntityName}</strong> and unlink related deals.
          </p>
        </ConfirmDeleteDialog>
      );
    } else {
      handleRemoveTheme(themeToRemove, parentTheme);
    }
  };

  useEffect(() => {
    if (!defaultValue?.length) return;

    const themes = defaultValue.map((v) => ({
      ...v,
      object: v,
      object_type: RelationObjectType.THEME,
    }));

    setValue("themes" as Path<T>, themes as any, { shouldDirty: false });
  }, [defaultValue?.length]);

  return (
    <Controller
      control={control}
      name={"themes" as Path<T>}
      render={({ field: { value }, fieldState: { error } }) => {
        const selectedThemes = (value as IThemeOption[]) || [];

        return (
          <div className="flex flex-col gap-1">
            <ThemeComponentWithThemes
              onAddTheme={handleAddTheme}
              onRemoveTheme={handleOpenConfirmDeleteDialog}
              selectedThemes={selectedThemes}
              // New entity: render the trigger as a bordered, transparent input. A created
              // company (relatedEntityName set) keeps the default gray-filled trigger.
              triggerClassName={
                relatedEntityName
                  ? undefined
                  : "bg-transparent border-stroke-primary dark:bg-gray-850 dark:border-stroke-primaryDark"
              }
            />
            <FieldErrorMessage message={error?.message} className="mt-0" />
          </div>
        );
      }}
    />
  );
}
